
import { useMemo } from 'react';
import { Transaction, TransactionFilters } from "@/types/payments";
import { isZeroAmountTransaction } from "@/utils/zeroAmountTransaction";

/**
 * Applies the filters that can't be pushed down to the DB query (issue #363 review follow-up).
 * Description/amount/date filters and the account selection are server-side (see
 * useTransactionLoader.ts) and operate over the full history — only `matched` (derived per-row,
 * not a stored column) remains here, scoped to whatever page is currently loaded.
 */
export function useTransactionFilter(
  transactions: Transaction[],
  filters: TransactionFilters,
  selectedTab: string,
) {
  // Filter transactions based on current filter state and selected tab
  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      // Filter by tab selection
      if (selectedTab === "matched" && !transaction.matched) {
        return false;
      }
      // A 0,00 transaction has nothing to match, so it is never outstanding (FIN-804).
      const outstanding = !transaction.matched && !isZeroAmountTransaction(transaction);
      if (selectedTab === "unmatched" && !outstanding) {
        return false;
      }

      // Only apply matched filter from the filter panel if not already filtered by tab
      if (selectedTab === "all") {
        if (filters.matched === 'matched' && !transaction.matched) {
          return false;
        }
        if (filters.matched === 'unmatched' && !outstanding) {
          return false;
        }
      }

      return true;
    });
  }, [transactions, filters, selectedTab]);

  return filteredTransactions;
}
